import { RecentOrderDto } from '../../api-types/models/OrderDto';
import { OrderStatisticsDto } from '../../api-types/models/OrderStatisticsDto';
import { DEFAULT_FLUSH_MS, MIN_FLUSH_MS } from './constants';
import type { ObserverParams } from './types';

/**
 * Parse URL search params to extract observer configuration.
 * @internal exported for tests
 */
export function parseObserverParams(search: string): ObserverParams {
    const param = new URLSearchParams(search);
    const theme = param.get('theme') ?? 'light';
    const raw = param.get('refreshInterval');
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    // Clamp to minimum if a finite number was provided, otherwise use default
    const refreshIntervalMs = Number.isFinite(parsed)
        ? Math.max(MIN_FLUSH_MS, parsed)
        : DEFAULT_FLUSH_MS;
    const snapshotMode = param.get('mode') === 'snapshot';
    // This page is exclusively used on e-ink displays; hints are always active.
    const einkHints = true;
    return { theme, refreshIntervalMs, snapshotMode, einkHints };
}

/**
 * Compare two order lists for shallow equality on orderItemId and displayName.
 * @internal exported for tests
 */
export function ordersListEqual(
    a: RecentOrderDto[],
    b: RecentOrderDto[]
): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (
            a[i].orderItemId !== b[i].orderItemId ||
            a[i].displayName !== b[i].displayName
        ) {
            return false;
        }
    }
    return true;
}

/**
 * Compare two statistics objects for equality on all relevant fields.
 * @internal exported for tests
 */
export function statisticsEqual(
    a: OrderStatisticsDto | undefined,
    b: OrderStatisticsDto | undefined
): boolean {
    if (a === b) {
        return true;
    }
    if (a == null || b == null) {
        return a === b;
    }
    return (
        a.completed === b.completed &&
        a.inProgress === b.inProgress &&
        a.ordered === b.ordered &&
        a.date === b.date
    );
}
